import { useEffect,useRef } from 'react';
import { cloneDeep } from 'lodash-es';
import { useDebounceFn } from 'ahooks';
import { ChartType } from '@intelligence/pages/TopCharts/_config/enums';

import type { IChartsIndexInstance } from '@intelligence/components/ChartsComponents/ChartsIndex';


const toSeriesList = (series: any): any[] => {
  if (Array.isArray(series)) return series;
  if (series) return [series];
  return [];
};

const patchPieEmphasis = (series: any) =>
  toSeriesList(series).map((serie: any) => ({
    ...serie,
    emphasis: {
      ...(serie.emphasis || {}),
      ...{
        scale: false,
        focus: 'none',
        disabled: true,
      }
    },
  }));

export default function usePieChartsHoverEffect(props: {
  loading: boolean;
  chartType: ChartType;
  chartsInstance: React.MutableRefObject<IChartsIndexInstance>;
  setOption: React.Dispatch<React.SetStateAction<any>>;
}) {
  const { loading, chartType, chartsInstance, setOption } = props;
  const needShow = useRef(false);
  // console.log('usePieChartsHoverEffect',props);

  const getOption = () => {
    const optionTmp = chartsInstance?.current?.getPieInstance()?.getOption();
    return optionTmp ? cloneDeep(optionTmp) : null;
  };

  const { run: updateDataItemStyle } = useDebounceFn((getDataItemStyle: (serieIndex: number, name: string) => any) => {
    const optionTmp = getOption();
    if (!optionTmp) return;
    optionTmp?.legend?.forEach((legend: any) => {
      if (!legend) return;
      legend.itemStyle = {
        ...(legend.itemStyle || {}),
        opacity: 1,
      };
    });
    optionTmp?.series?.forEach((serie: any, serieIndex: number) => {
      if (!Array.isArray(serie?.data)) return;
      serie.data.forEach((item: any) => {
        if (!item) return;
        item.itemStyle = {
          ...(item.itemStyle || {}),
          ...getDataItemStyle(serieIndex, item.name),
        };
      });
    });
    const pieChartInstance = chartsInstance?.current?.getPieInstance();
    pieChartInstance.setOption({
      series: patchPieEmphasis(optionTmp.series),
    });
  }, { wait: 0 });

  const updateHighlightState = (name: string, serieIndex?: number) => {
    // 是否指定serie 更新样式（指定serie的索引，鼠标移入图例时 不指定serie，鼠标移入某个圆环图时，需要指定该圆环图对应的serie）
    const isLockSerie = typeof serieIndex === 'number' && !Number.isNaN(serieIndex);
    updateDataItemStyle((serieIndexInner: number, namePayload: string) => {
      const opacity = (() => {
        // 鼠标移入某个圆环图，并且 当前圆环图 不是 鼠标移入这个圆环图时，直接返回1
        if (isLockSerie && serieIndex !== serieIndexInner) return 1;
        return namePayload === name ? 1 : 0.3;
      })();

      return {
        opacity,
      };
    });
  };
  const resetHighlightState = () => {
    updateDataItemStyle(() => ({
      opacity: 1,
    }));
  };

  const handleMouseOver = (payload: any) => {
    const name = payload?.name || payload?.data?.name;
    const serieIndex = payload?.seriesIndex;
    if (!name) return;
    if(needShow.current) return;
    needShow.current = true;
    updateHighlightState(name, serieIndex);
  };

  const handleMouseOut = () => {
    needShow.current = false;
    resetHighlightState();
  };

  const handleHighlight = (payload: any) => {
    const optionTmp = getOption();
    const legendSelectedTmp = optionTmp?.legend?.[0]?.selected || {};
    const name = payload?.name;
    if (!name) return;
    if (Reflect.has(legendSelectedTmp, name) && !legendSelectedTmp?.[name]) return;
    updateHighlightState(name);
  };

  const handleDownplay = (payload: any) => {
    const optionTmp = getOption();
    const legendSelectedTmp = optionTmp?.legend?.[0]?.selected || {};
    const name = payload?.name;
    if (!name) return;
    if (Reflect.has(legendSelectedTmp, name) && !legendSelectedTmp?.[name]) return;
    setTimeout(() => {
      resetHighlightState();
    });
  };

  useEffect(() => {
    if (loading) return;
    if (chartType !== ChartType.Pie) return;
    const pieChartInstance = chartsInstance?.current?.getPieInstance();
    if (!pieChartInstance) return;

    const initSeries = patchPieEmphasis(pieChartInstance.getOption()?.series);
    if (initSeries.length > 0) {
      pieChartInstance.setOption({ series: initSeries }, false);
    }

    pieChartInstance?.on('mouseover', handleMouseOver);
    pieChartInstance?.on('mouseout', handleMouseOut);
    pieChartInstance?.on('highlight', handleHighlight);
    pieChartInstance?.on('downplay', handleDownplay);

    return () => {
      pieChartInstance?.off('mouseover', handleMouseOver);
      pieChartInstance?.off('mouseout', handleMouseOut);
      pieChartInstance?.off('highlight', handleHighlight);
      pieChartInstance?.off('downplay', handleDownplay);
    };
  }, [loading, chartType]);

  return {
    onHighlight: handleHighlight,
    onDownplay: handleDownplay,
  };
}
